/**
 * AdminAuthContext
 *
 * Manages admin authentication state. Persists via localStorage
 * using "admin_token" and "admin_user" keys — fully separate from
 * the user frontend's "token" / "user" keys.
 *
 * After login it verifies user_type === 'admin'; if a non-admin
 * credential is used, the token is discarded immediately.
 *
 * Auto-logs out after 6 hours from login time.
 *
 * Exposes `admin.admin_role` and a convenience `hasRole(...roles)` helper.
 */
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';

const AdminAuthContext = createContext(null);

const SESSION_MAX_MS = 6 * 60 * 60 * 1000; // 6 hours in ms

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return ctx;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const logoutTimerRef          = useRef(null);

  // ── Session timer ─────────────────────────────────────────────
  const clearLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const scheduleAutoLogout = useCallback((loginTimestamp) => {
    clearLogoutTimer();
    const elapsed = Date.now() - loginTimestamp;
    const remaining = SESSION_MAX_MS - elapsed;

    if (remaining <= 0) {
      // Already expired
      _clearStorage();
      setAdmin(null);
      return;
    }

    logoutTimerRef.current = setTimeout(() => {
      _clearStorage();
      setAdmin(null);
      // Page will redirect to login via the route guard
    }, remaining);
  }, [clearLogoutTimer]);

  // ── Restore session on mount ──────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const saved = localStorage.getItem('admin_user');
    const loginAt = parseInt(localStorage.getItem('admin_login_at') || '0', 10);

    if (token && saved) {
      // Check if session has expired
      if (loginAt && (Date.now() - loginAt) > SESSION_MAX_MS) {
        _clearStorage();
        setLoading(false);
        return;
      }

      try {
        const parsed = JSON.parse(saved);
        setAdmin(parsed);

        // Schedule remaining auto-logout time
        if (loginAt) {
          scheduleAutoLogout(loginAt);
        }

        // Re-fetch profile to get latest data (e.g. admin_role added after login)
        authAPI.getMe()
          .then(res => {
            const fresh = res.data;
            if (fresh && fresh.user_type === 'admin') {
              localStorage.setItem('admin_user', JSON.stringify(fresh));
              setAdmin(fresh);
            }
          })
          .catch(() => {
            // Token may be expired — clear and redirect
            _clearStorage();
            clearLogoutTimer();
            setAdmin(null);
          });
      } catch {
        _clearStorage();
      }
    }
    setLoading(false);
  }, [scheduleAutoLogout, clearLogoutTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearLogoutTimer();
  }, [clearLogoutTimer]);

  // ── Helpers ───────────────────────────────────────────────────
  const _clearStorage = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_login_at');
  };

  // ── Login ─────────────────────────────────────────────────────
  const login = useCallback(async (credentials) => {
    setError(null);
    try {
      const loginRes = await authAPI.login(credentials);
      const { access_token, user_type } = loginRes.data;

      // Reject non-admin accounts immediately
      if (user_type !== 'admin') {
        return { success: false, error: 'Access denied — admin accounts only.' };
      }

      // Persist token first so the /auth/me call is authorised
      const now = Date.now();
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_login_at', String(now));

      // Fetch full profile (includes admin_role)
      const meRes  = await authAPI.getMe();
      const fullAdmin = meRes.data;

      // Double-check profile (belt + suspenders)
      if (fullAdmin.user_type !== 'admin') {
        _clearStorage();
        return { success: false, error: 'Access denied — admin accounts only.' };
      }

      localStorage.setItem('admin_user', JSON.stringify(fullAdmin));
      setAdmin(fullAdmin);

      // Start 6-hour auto-logout timer
      scheduleAutoLogout(now);

      return { success: true };
    } catch (err) {
      _clearStorage();
      const msg = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(msg);
      return { success: false, error: msg };
    }
  }, [scheduleAutoLogout]);

  // ── Logout ────────────────────────────────────────────────────
  const logout = useCallback(() => {
    _clearStorage();
    clearLogoutTimer();
    setAdmin(null);
  }, [clearLogoutTimer]);

  // ── Role helper ───────────────────────────────────────────────
  // Usage: hasRole('super_admin', 'marketing') → true if admin has either role
  // Backward compat: if admin_role is null but user_type is 'admin', treat as super_admin
  const hasRole = useCallback((...roles) => {
    if (!admin) return false;
    const effectiveRole = admin.admin_role || (admin.user_type === 'admin' ? 'super_admin' : null);
    if (!effectiveRole) return false;
    return roles.includes(effectiveRole);
  }, [admin]);

  // Convenience: is this admin super_admin?
  const isSuperAdmin = admin?.admin_role === 'super_admin' || (admin?.user_type === 'admin' && !admin?.admin_role);

  const value = {
    admin,
    isAuthenticated: !!admin,
    loading,
    error,
    login,
    logout,
    hasRole,
    isSuperAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
